<template>
	<div class="d-council-member-list-custom">
		<ListPageLayout
			ref="pageLayout"
			v-bind="{
				...$attrs,
				...$props,
			}"
			:search-config="{
				limit: 12,
			}"
		>
			<!-- The search filters -->
			<template #filters="{ filters, filterValues, updateValue }">
				<div
					v-if="filters && (filters.party || filters.committee)"
					class="mt-3xl flex flex-wrap gap-x-layout-gutter gap-y-sm"
				>
					<BaseInputDropdown
						v-if="filters.party"
						id="dropdown-party"
						class="w-full >=1024:w-6/12col z-20"
						v-bind="filters.party"
						:options="filters.party.value"
						:value="filterValues.party"
						:placeholder="filters.party.placeholder"
						@input="(value) => updateValue('party', value)"
					/>
					<BaseInputDropdown
						v-if="filters.committee"
						id="dropdown-committee"
						class="w-full >=1024:w-6/12col z-10"
						v-bind="filters.committee"
						:options="filters.committee.value"
						:value="filterValues.committee"
						:placeholder="filters.committee.placeholder"
						@input="(value) => updateValue('committee', value)"
					/>
				</div>
			</template>

			<template #resultsStatus="{ pagination }">
				Vi har fundet
				{{ pagination && pagination.total ? pagination.total : 0 }}
				byrådsmedlem{{
					pagination && pagination.total === 1 ? '' : 'mer'
				}}
			</template>

			<template #results="{ data }">
				<div
					v-if="data && data.length"
					key="content"
					class="
						grid
						gap-layout-gutter
						grid-cols-1
						>=768:grid-cols-2
						>=1200:grid-cols-3
					"
				>
					<ContentCard
						v-for="item in data"
						:key="item.key"
						class="h-full"
						v-bind="{
							linkTag: 'button',
							image: item.image,
							useFallbackImage: 3,
							title: `${item.name} ${
								item.party
									? '(' + item.party.partyLetter + ')'
									: ''
							}`,
							teaser: item.teaser,
							link: { url: item.url },
						}"
						@click="setActiveTarget(item, true)"
					>
						<template #button>
							<BaseButton
								v-if="item.image && item.image.url"
								tag="NuxtLinkExt"
								:to="item.image.url"
								class="absolute right-md top-md w-40 h-40"
								inner-class="border-0"
								target="_blank"
								aria-label="Download billede"
								download
								light
							>
								<template #icon>
									<SvgIconDownload class="w-15 h-18" />
								</template>
							</BaseButton>
						</template>

						<template #before>
							<div class="">
								<div
									v-if="item.title"
									class="
										font-darker-grotesque font-semibold
										mb-md
									"
									v-text="item.title"
								></div>
							</div>
						</template>
						<template #after>
							<div class="flex items-center space-x-12 mt-lg">
								<div
									:class="[
										'flex items-center justify-center',
										'w-24 h-24 rounded-full bg-transparent text-black  ',
									]"
									style="border-width: 1.5px"
								>
									<SvgIconCaret
										class="
											w-12
											transform
											-rotate-90
											translate-x-1
										"
									/>
								</div>

								<span
									class="
										font-bold font-darker-grotesque
										uppercase
										text-button-xs
									"
									v-text="'Læs mere'"
								></span>
							</div>
						</template>
					</ContentCard>
				</div>
			</template>

			<!-- Overlay -->
			<Portal to="overlay">
				<Transition name="t-council-member-list-custom__overlay" appear>
					<div
						v-if="activeTarget"
						class="bg-white bg-opacity-70 w-full h-full"
						@click="() => setActiveTarget(null)"
					>
						<div
							class="
								h-full
								bg-secondary
								absolute
								right-0
								box-content
								w-320
								px-layout-margin
							"
							@click.stop
						>
							<BaseButton
								light
								class="absolute right-md top-md"
								inner-class="bg-secondary"
								@click="() => setActiveTarget(null)"
							>
								<template #icon>
									<SvgIconClose class="w-12" />
								</template>
								<span>luk</span>
							</BaseButton>
							<div>
								<div
									class="
										c-council-member-list__overlay-member-info-wrapper
										<375:grid
									"
								>
									<div class="mt-4xl <375:w-3/5col">
										<div v-if="activeTarget.title">
											<span
												class="
													font-semibold
													font-darker-grotesque
													text-label-card
													wrapper
												"
												v-text="activeTarget.title"
											></span>
										</div>
										<BaseH3
											tag="h2"
											class="mb-lg font-bold"
											v-text="activeTarget.name"
										>
										</BaseH3>
									</div>
									<div
										class="
											c-council-member-list__overlay-member-info
											min-h-fit
										"
									>
										<!-- Party -->
										<div
											v-if="activeTarget.party.partyName"
											class="mt-lg flex items-baseline"
										>
											<SvgIconStar
												class="
													mr-md
													w-18
													relative
													top-3
												"
												style="height: 1em"
											/>
											<span
												v-text="
													activeTarget.party.partyName
												"
											></span>
										</div>
										<!-- Email -->
										<a
											v-if="activeTarget.email"
											:href="`mailto:${activeTarget.email}`"
											class="flex mt-lg items-baseline"
										>
											<SvgIconEmail
												class="
													w-18
													mr-md
													relative
													top-3
												"
												style="height: 1em"
											/>

											<span
												v-text="activeTarget.email"
											></span>
										</a>
										<!-- Phone -->
										<a
											v-if="activeTarget.phone"
											:href="`mailto:${activeTarget.phone}`"
											class="flex mt-lg items-baseline"
										>
											<SvgIconPhone
												class="
													w-18
													mr-md
													relative
													top-3
												"
												style="height: 1em"
											/>
											<span
												v-text="activeTarget.phone"
											></span>
										</a>
										<!-- Mobile -->
										<a
											v-if="activeTarget.mobile"
											:href="`mailto:${activeTarget.mobile}`"
											class="flex mt-lg items-baseline"
										>
											<SvgIconMobile
												class="
													w-18
													mr-md
													relative
													top-3
												"
												style="height: 1em"
											/>
											<span
												v-text="activeTarget.mobile"
											></span>
										</a>
										<!-- Address -->

										<div
											v-if="
												activeTarget.address.address ||
												activeTarget.address
													.postalCode ||
												activeTarget.address.city
											"
											class="flex mt-lg items-baseline"
										>
											<SvgIconPin
												class="
													w-18
													mr-md
													relative
													top-3
												"
												style="height: 1em"
											/>
											<span
												v-html="
													[
														activeTarget.address
															.address,
														[
															activeTarget.address
																.postalCode,
															activeTarget.address
																.city,
														]
															.filter(Boolean)
															.join(' '),
													]
														.filter(Boolean)
														.join('<br/>')
												"
											>
											</span>
										</div>
									</div>
									<div v-if="activeTarget.text">
										<div
											class="
												w-full
												border-opacity-5
												border-black
												border-t-2
												m-auto
												pt-md
												mt-md
												<375:w-4/5
											"
										>
											<div class="mb-lg <375:w-3/5col">
												<p
													v-html="activeTarget.text"
												></p>
											</div>
										</div>
									</div>
									<div>
										<div
											v-if="
												activeTarget.committees
													.chairman ||
												activeTarget.committees
													.viceChairman ||
												activeTarget.committees.member
											"
											class="
												w-full
												border-opacity-5
												border-black
												border-t-2
												m-auto
												pt-xl
												mt-2xl
												<375:w-4/5
											"
										>
											<!-- Comittees -->
											<div
												v-if="
													activeTarget.committees
														.chairman ||
													activeTarget.committees
														.viceChairman ||
													activeTarget.committees
														.member
												"
												class="mb-lg <375:w-3/5col"
											>
												<BaseH3
													tag="h3"
													class="mb-lg font-bold"
													v-text="'Medlem af'"
												></BaseH3>

												<ul>
													<li
														v-for="(
															item, index
														) in activeTarget
															.committees
															.chairman"
														:key="`comittee-${index}`"
														class="
															flex
															mt-20
															mb-md
															font-darker-grotesque
															font-semibold
															text-button-sm
															group
														"
													>
														<SvgIconArrow
															class="
																flex-shrink-0
																w-18
																h-13.5
																mr-md
																mt-4
															"
														></SvgIconArrow>
														<NuxtLinkExt
															:class="[
																'duration-500 ease-smooth-out',
																'transform group-hover:translate-x-8',
																'pr-8',
															]"
															:to="`?committee=${item.key.replace(
																/[-]/g,
																''
															)}`"
															:target="
																item.target
															"
															v-html="
																`${item.name} (formand)`
															"
														></NuxtLinkExt>
													</li>
													<li
														v-for="(
															item, index
														) in activeTarget
															.committees
															.viceChairman"
														:key="`comittee-${index}`"
														class="
															flex
															mt-20
															mb-md
															font-darker-grotesque
															font-semibold
															text-button-sm
															group
														"
													>
														<SvgIconArrow
															class="
																flex-shrink-0
																w-18
																h-13.5
																mr-md
																mt-4
															"
														></SvgIconArrow>
														<NuxtLinkExt
															:class="[
																'duration-500 ease-smooth-out',
																'transform group-hover:translate-x-8',
																'pr-8',
															]"
															:to="`?committee=${item.key.replace(
																/[-]/g,
																''
															)}`"
															:target="
																item.target
															"
															v-html="
																`${item.name} (næstformand)`
															"
														></NuxtLinkExt>
													</li>
													<li
														v-for="(
															item, index
														) in activeTarget
															.committees.member"
														:key="`comittee-${index}`"
														class="
															flex
															mt-20
															mb-md
															font-darker-grotesque
															font-semibold
															text-button-sm
															group
														"
													>
														<SvgIconArrow
															class="
																flex-shrink-0
																w-18
																h-13.5
																mr-md
																mt-4
															"
														></SvgIconArrow>
														<NuxtLinkExt
															:class="[
																'duration-500 ease-smooth-out',
																'transform group-hover:translate-x-8',
																'pr-8',
															]"
															:to="`?committee=${item.key.replace(
																/[-]/g,
																''
															)}`"
															:target="
																item.target
															"
															v-html="item.name"
														></NuxtLinkExt>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Transition>
			</Portal>
		</ListPageLayout>

		<FeedbackModule
			v-if="!hideFeedback"
			:feedback="feedback"
			class="mt-7xl"
		/>
	</div>
</template>

<script>
import ListPageLayout from '~/components/shared/ListPageLayout';
import ContentCard from '~/components/shared/ContentCard';
import FeedbackModule from '~/components/shared/FeedbackModule';
import SvgIconCaret from '~/assets/svgs/icon-caret.svg?inline';
import SvgIconDownload from '~/assets/svgs/icon-download.svg?inline';
import SvgIconEmail from '~/assets/svgs/icon-email.svg?inline';
import SvgIconMobile from '~/assets/svgs/icon-mobile.svg?inline';
import SvgIconPhone from '~/assets/svgs/icon-phone.svg?inline';
import SvgIconPin from '~/assets/svgs/icon-pin.svg?inline';
import SvgIconStar from '~/assets/svgs/icon-star.svg?inline';
import SvgIconClose from '~/assets/svgs/icon-close.svg?inline';
import SvgIconArrow from '~/assets/svgs/icon-arrow.svg?inline';

export default {
	name: 'MainCouncilMemberListCustom',

	components: {
		ListPageLayout,
		ContentCard,
		FeedbackModule,
		SvgIconDownload,
		SvgIconCaret,
		SvgIconEmail,
		SvgIconMobile,
		SvgIconPhone,
		SvgIconPin,
		SvgIconStar,
		SvgIconClose,
		SvgIconArrow,
	},

	inheritAttrs: false,

	props: {
		hideFeedback: {
			type: Boolean,
			default: false,
		},
		feedback: Object,
	},

	data() {
		return {
			activeTarget: null,
			close: false,
		};
	},

	methods: {
		setActiveTarget(target) {
			this.activeTarget = target;
		},
	},
};
</script>
<style lang="postcss">
.t-council-member-list-custom__overlay-enter-active,
.t-council-member-list-custom__overlay-leave-active {
	@apply duration-300 ease-smooth-line;
}

.t-council-member-list-custom__overlay-enter-active > * {
	@apply duration-300 ease-smooth-out;
}
.t-council-member-list-custom__overlay-leave-active > * {
	@apply duration-300 ease-smooth-in;
}

.t-council-member-list-custom__overlay-enter,
.t-council-member-list-custom__overlay-leave-to {
	@apply bg-opacity-0 !important;
}

.t-council-member-list-custom__overlay-enter > *,
.t-council-member-list-custom__overlay-leave-to > * {
	transform: translate3d(100%, 0, 0);
}
</style>
